"use client";

import {
  CaseListData,
  CaseListError,
  MDICaseID,
  UnreadMessageInfo,
  getUnreadMessageInfo,
} from "@repo/provider-network";
import { useInitialContext } from "@repo/sdk/provider";
import {
  BrandedEmptyState,
  BrandedNewVisitCTA,
  CurrentVisitsHeader,
  SeePastVisitsButton,
} from "@repo/ui/branded";
import { HeadlineBodyText } from "src/components/KinsaTypography";
import { KinsaInitialContext } from "src/lib/definitions";
import { SubscriptionState } from "src/lib/subscription-gate";
import { derivePastVisitDisplay } from "./past-visit-display";
import PastVisitItem from "./past-visit-item";
import VisitListEmpty from "./states/visit-list-empty";
import SubscriptionBanner from "./subscription-banner";
import VisitListError from "./visit-list-error";
import VisitListItem, {
  type VisitCardBrandConfig,
  type VisitHrefResolver,
} from "./visit-list-item";
import VisitListLoading from "./visit-list-loading";

interface VisitListPureProps {
  visits?: CaseListData;
  isLoading?: boolean;
  error?: Error | CaseListError;
  unreadMap?: Map<MDICaseID, UnreadMessageInfo>;
  className?: string;
  showEmptyState?: boolean;
  onStartVisit?: () => void;
  onExplorePlans?: () => void;
  hasSubscription?: boolean;
  subscriptionState?: SubscriptionState;
  startingVisit?: boolean;
  /** Brand-specific visit href resolver. Falls back to default (Kinsa). */
  getVisitHref?: VisitHrefResolver;
  /** Brand-specific card config. Drives both the active visit cards and
   *  the past-visit rows (PastVisitItem). Falls back to Kinsa defaults. */
  brandConfig?: VisitCardBrandConfig;
}

/**
 * Pure visit list component that renders visit data without side effects
 * Perfect for design review and playbook usage
 */
const VisitListPure: React.FC<VisitListPureProps> = ({
  visits,
  isLoading = false,
  error,
  unreadMap,
  className = "",
  showEmptyState = true,
  onStartVisit,
  onExplorePlans,
  hasSubscription,
  subscriptionState,
  startingVisit = false,
  getVisitHref,
  brandConfig,
}) => {
  const context = useInitialContext<KinsaInitialContext>();
  const account = context?.account;
  const coveredPersons = account?.coveredPersons;

  // Show error state with retry option
  if (error && !visits) {
    return (
      <div className={className}>
        <VisitListError
          error={
            error instanceof Error ? error : new Error("An error occurred")
          }
        />
      </div>
    );
  }

  // Loading skeleton: either the initial fetch or revalidation after a cache
  // bust (e.g. exiting the Blueberry iframe — see intake/.../blueberry-visit-
  // step.tsx and visit/.../visit-frame.tsx, which call mutate(undefined,
  // { revalidate: true }) so isLoading flips back to true). Avoids a
  // discouraging "no visits" flash while a refetch is in flight. We still
  // require `isLoading` so users without a SWR key (e.g. hasTerms=false) fall
  // through to the empty state instead of getting stuck on the skeleton.
  if (isLoading && !visits) {
    return (
      <div className={className}>
        <VisitListLoading />
      </div>
    );
  }

  // Show empty state
  const isEmpty = !visits || (!visits.current.length && !visits.past.length);
  if (isEmpty) {
    if (!showEmptyState) return null;
    return (
      <VisitListEmpty
        onStartVisit={onStartVisit}
        onExplorePlans={onExplorePlans}
        hasSubscription={hasSubscription}
      />
    );
  }

  const inlinePastVisits = brandConfig?.inlinePastVisits ?? true;
  const hasCurrentVisits = visits.current.length > 0;
  const hasPastVisits = visits.past.length > 0;
  const pastOnlyWithGetCare = !hasCurrentVisits && hasPastVisits;
  // Kinsa inlines past visits when there's no current visit; Owlet never inlines (PE-1374).
  const showPastVisitsList =
    inlinePastVisits && hasPastVisits && !hasCurrentVisits;
  const showCurrentVisitsList = hasCurrentVisits;
  // Owlet shows the link whenever there are past visits; Kinsa's button is a
  // no-op, so its gate stays the prior current+past condition.
  const showSeePastVisitsLink = inlinePastVisits
    ? hasPastVisits && hasCurrentVisits
    : hasPastVisits;

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Subscription cancellation banner */}
      {(subscriptionState === "cancelled" ||
        subscriptionState === "canceling") && <SubscriptionBanner />}

      {/* TODO(sid-review): when subscriptionState === "cancelled" AND pastOnlyWithGetCare,
          the banner and the "How can we help?" hero render together — needs design decision
          on whether to suppress the hero in cancelled state. */}
      {/* Past-only hero: brand-specific CTA to start a new visit */}
      {pastOnlyWithGetCare && onStartVisit && (
        <BrandedEmptyState
          onStartVisit={onStartVisit}
          isProcessing={startingVisit}
          variant="returning"
        />
      )}

      {/* Active/Requested visit cards */}
      {showCurrentVisitsList && <CurrentVisitsHeader isLoading={isLoading} />}
      {showCurrentVisitsList && (
        <div
          className={`space-y-4 ${isLoading ? "animate-pulse opacity-70" : ""}`}
        >
          {visits.current.map((visit, index) => (
            <VisitListItem
              key={visit.case_id || visit.id || `visit-${index}`}
              visit={visit}
              unreadInfo={getUnreadMessageInfo(visit, unreadMap)}
              coveredPersons={coveredPersons}
              patientMapping={visits.patients}
              getVisitHref={getVisitHref}
              brandConfig={brandConfig}
            />
          ))}
        </div>
      )}

      {/* New Visit button */}
      {showCurrentVisitsList && onStartVisit && (
        <BrandedNewVisitCTA
          onStartVisit={onStartVisit}
          isProcessing={startingVisit}
        />
      )}

      {/* "See past visits" link (brand-specific: Owlet shows → navigates to
          the dedicated /past-visits route; Kinsa renders null). */}
      {showSeePastVisitsLink && <SeePastVisitsButton />}

      {/* History section (past visits) */}
      {showPastVisitsList && (
        <section className="flex flex-col gap-2">
          <HeadlineBodyText
            className={`text-text-200 ${isLoading ? "animate-pulse opacity-60" : ""}`}
          >
            History
          </HeadlineBodyText>
          <div
            className={`space-y-2 ${isLoading ? "animate-pulse opacity-70" : ""}`}
          >
            {visits.past.map((visit, index) => (
              <PastVisitItem
                key={visit.case_id || visit.id || `past-${index}`}
                {...derivePastVisitDisplay(
                  visit,
                  coveredPersons,
                  visits.patients
                )}
                brandConfig={brandConfig}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default VisitListPure;
